import React, { useCallback, useEffect, useRef, useState } from 'react';
import { throttle } from 'lodash';
import { Modal, message } from 'antd';
import { UAParser } from 'ua-parser-js';
import {
  getPushSubscription,
  subscribeToPushNotifications,
  unsubscribeFromPushNotifications,
} from 'shared/utils/push-notifications';
import { selectActiveOrg, selectActiveUser } from 'shared/store/auth-slice';
import { useAppSelector } from 'shared/store';
import { Page, PageParams } from 'types/pagination';
import useResizeObserver from '@react-hook/resize-observer';
import { selectActiveVisitor } from 'shared/store/shared-selectors';

/**
 * Prevent accidental unloading, optionally only when a passed condition is true.
 */
export function useBeforeUnload(condition = true) {
  useEffect(() => {
    if (!condition) {
      return;
    }
    const handler = (ev: BeforeUnloadEvent) => {
      ev.preventDefault();
      ev.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => {
      window.removeEventListener('beforeunload', handler);
    };
  }, [condition]);
}

/**
 * Check if currently authed user has permission to perform an action.
 */
export function useHasOrgPermission(entity: string, action: string) {
  const user = useAppSelector(selectActiveUser);
  if (!user) return false;
  const perms = user.orgPermissions;
  return !!(perms && perms[entity] && perms[entity].includes(action));
}

/**
 * Store the previous value of a variable
 */
export function usePrevious<T>(value: T) {
  const ref = useRef<T>();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}

/**
 * Interval hook
 */
export function useInterval(cb: () => void, delay: number) {
  const callback = useRef<() => void>();
  useEffect(() => {
    callback.current = cb;
  }, [cb]);
  useEffect(() => {
    if (delay) {
      const interval = window.setInterval(() => {
        if (callback.current) {
          callback.current();
        }
      }, delay);
      return () => {
        clearInterval(interval);
      };
    }
  }, [delay]);
}

export const usePushSubscriptionState = () => {
  const [isUnsubscribed, setIsUnsubscribed] = useState(false);
  const [needsConfirmation, setNeedsConfirmation] = useState(false);
  const [isChangingSubscription, setIsChangingSubscription] = useState(false);
  const isSupported = 'serviceWorker' in navigator && 'PushManager' in window;

  useEffect(() => {
    if (!isSupported) return;
    getPushSubscription().then(sub => {
      setIsUnsubscribed(!sub);
    });
  }, [isSupported]);

  const handleSubscribe = useCallback(async () => {
    setIsChangingSubscription(true);
    await subscribeToPushNotifications()
      .then(() => setIsUnsubscribed(false))
      .then(() => setNeedsConfirmation(true))
      .catch(err => {
        // Handle error message that means brave has notifs disabled
        // Longer than usual, so have to set some inline styles to allow text wrapping
        if (err?.message === 'Registration failed - push service error') {
          message.error(
            <span style={{ display: 'inline-block', maxWidth: 400 }}>
              Brave’s privacy settings do not allow push notifications by default, please
              go to brave://settings/privacy, enable push messaging, and restart your
              browser to enable them.
            </span>,
            10,
          );
          return;
        }
        message.error(err.message);
      });
    setIsChangingSubscription(false);
  }, []);

  const handleUnsubscribe = useCallback(async () => {
    setIsChangingSubscription(true);
    await unsubscribeFromPushNotifications()
      .then(() => setIsUnsubscribed(true))
      .catch(err => {
        // Ignore 404s, the sub is already gone
        if (err?.response?.status === 404) {
          setIsUnsubscribed(true);
          return;
        }
        message.error(err.message);
      });
    setIsChangingSubscription(false);
  }, []);

  return {
    isSupported,
    isUnsubscribed,
    isChangingSubscription,
    handleSubscribe,
    handleUnsubscribe,
    needsConfirmation,
    setNeedsConfirmation,
  };
};

export const useUserAgent = (ua: string | null | undefined) => {
  // Send 'unknown' instead of empty string, else it will default to browser UA
  const [userAgent, setUserAgent] = useState(new UAParser(ua || 'unknown').getResult());
  useEffect(() => {
    setUserAgent(new UAParser(ua || 'unknown').getResult());
  }, [ua]);
  return userAgent;
};

/**
 * Get a ref to a value that updates whenever value changes. Useful for functions
 * or values that need to be used in a useEffect, but shouldn't re-run the effect.
 */
export function useUpdatingRef<T>(value: T) {
  const ref = useRef<T>(value);
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref;
}

export const useNotificationsNotSupportedModal = () => {
  return () => {
    return Modal.warning({
      width: 600,
      title: `Uh oh, notifications are not supported on this browser`,
      content: (
        <>
          <p style={{ paddingTop: '24px' }}>
            Please use one of these browsers if you'd like to receive desktop
            notifications
          </p>
          <ul style={{ paddingLeft: '28px' }}>
            <li>Chrome</li>
            <li>Firefox</li>
            <li>Edge</li>
            <li>Opera</li>
          </ul>
        </>
      ),
    });
  };
};

/**
 * Window height and width.
 */
export const useWindowSize = () => {
  const [windowSize, setWindowSize] = useState({
    width: document?.documentElement?.clientWidth || 0,
    height: document?.documentElement?.clientHeight || 0,
  });

  useEffect(() => {
    const handleResize = () =>
      setWindowSize({
        width: document?.documentElement?.clientWidth || 0,
        height: document?.documentElement?.clientHeight || 0,
      });

    window.addEventListener('resize', handleResize, { passive: true });
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return windowSize;
};

/**
 * Element size
 */
export const useElementSize = (target: React.MutableRefObject<HTMLElement | null>) => {
  const [size, setSize] = useState<DOMRect>();
  React.useLayoutEffect(() => {
    target.current && setSize(target.current.getBoundingClientRect());
  }, [target]);
  useResizeObserver(target, entry =>
    requestAnimationFrame(() => setSize(entry.contentRect)),
  );
  return size;
};

/**
 * Scroll X and Y positions.
 */
export const useWindowScrollPosition = () => {
  const [position, setPosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleScroll = () => {
      setPosition({ x: window.scrollX, y: window.scrollY });
    };

    window.addEventListener('scroll', handleScroll);

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return position;
};

/**
 * Pagination utilities. Page fetcher should be memoized, because it will re-run
 * on reference change to allow for non-usePagination arguments.
 */
export function usePagination<T>(
  pageFetcher: (params: PageParams) => Promise<Page<T> | undefined>,
  runOnMount = true,
) {
  const [data, setData] = useState<Page<T>>();
  const [error, setError] = useState<string>();
  const [isFetching, setIsFetching] = useState(false);
  const [page, setPage] = useState(1);
  const hasFetchedRef = useRef(false);

  const refresh = useCallback(() => {
    hasFetchedRef.current = true;
    setIsFetching(true);
    pageFetcher({ page })
      .then(data => setData(data))
      .catch(err => {
        console.error('Failed to fetch paginated data', err);
        setError(err.message || 'Failed to fetch data');
      })
      .finally(() => setIsFetching(false));
  }, [page, pageFetcher]);

  // Whenever state mismatches data, run fetcher with state
  useEffect(() => {
    if (isFetching || error) return;
    if (!runOnMount && !hasFetchedRef.current) return;
    if (data && page === data?.page) return;
    // Run initial fetch immediately, slight delay on subsequent fetches to avoid spamming
    if (!hasFetchedRef.current) {
      refresh();
      return;
    }
    const timeout = setTimeout(() => refresh(), 30);
    return () => clearTimeout(timeout);
  }, [data, page, error, isFetching, runOnMount, refresh]);

  const goToPage = useCallback((page: number) => {
    setPage(page);
  }, []);

  const goToNextPage = useCallback(() => {
    setPage(p => p + 1);
  }, []);

  const goToPrevPage = useCallback(() => {
    setPage(p => Math.max(p - 1, 1));
  }, []);

  return {
    data,
    error,
    isFetching,
    refresh,
    goToPage,
    goToNextPage,
    goToPrevPage,
  };
}

/**
 * Provides a boolean that indicates if the current tab is hidden ("hidden" varies
 * from browser and OS) that will update when page visibility changes.
 */
export function useIsPageHidden() {
  const [isHidden, setIsHidden] = useState(
    document.visibilityState === 'hidden' || document.hidden,
  );

  useEffect(() => {
    const onVisibilityChange = () => {
      setIsHidden(document.visibilityState === 'hidden' || document.hidden);
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pageshow', onVisibilityChange);
    window.addEventListener('pagehide', onVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pageshow', onVisibilityChange);
      window.removeEventListener('pagehide', onVisibilityChange);
    };
  }, []);

  return isHidden;
}

export function useRrwebIngressHealth() {
  const {
    rrwebIngressLastDuplicateEventTime,
    rrwebIngressQueueLastDrainLatency,
  } = useAppSelector(s => s.dashboard);
  const [status, setStatus] = useState<'nominal' | 'congested'>('nominal');
  const [recencyTime, setRecencyTime] = useState(Date.now());

  // The timings here can be tuned as needed, ofc
  const isQueueCongested = rrwebIngressQueueLastDrainLatency > 2000;
  const isWidgetSendingDupesRecently =
    recencyTime - rrwebIngressLastDuplicateEventTime < 1000;

  useEffect(() => {
    if (isQueueCongested) {
      setStatus('congested');
    } else {
      setStatus('nominal');
    }
    let setRecencyTimeout = 0;
    if (isWidgetSendingDupesRecently) {
      // set the recencyTime in 3 seconds to trigger a re-check
      // (so it'll be on for 3 seconds and reset if it clears up)
      setRecencyTimeout = window.setTimeout(() => {
        setRecencyTime(Date.now());
      }, 15000);
    }
    return () => {
      clearTimeout(setRecencyTimeout);
    };
  }, [isWidgetSendingDupesRecently, isQueueCongested, recencyTime]);

  return status;
}

export function useThrottle(cb: (...args: any) => any, delay: number) {
  const options = { leading: true, trailing: true };
  const cbRef = useRef(cb);
  useEffect(() => {
    cbRef.current = cb;
  });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useCallback(
    throttle((...args: any) => cbRef.current(...args), delay, options),
    [delay],
  );
}

/**
 * Derived state for visitor spy optin
 */
export function useVisitorSpyOptinState() {
  const org = useAppSelector(selectActiveOrg);
  const visitor = useAppSelector(selectActiveVisitor);
  const [isRejectedOrUnanswered, setIsRejectedOrUnanswered] = useState(false);
  const [isShowOverlay, setIsShowOverlay] = useState(false);

  useEffect(() => {
    setIsRejectedOrUnanswered(
      visitor?.isSpyingRejected ||
        (!visitor?.isSpyingAccepted && !visitor?.isSpyingRejected),
    );
  }, [visitor]);

  useEffect(() => {
    setIsShowOverlay(org?.widgetSettings.viewing === 'ask' && isRejectedOrUnanswered);
  }, [org, isRejectedOrUnanswered]);

  return {
    isShowOverlay,
    isRejectedOrUnanswered,
    isRejected: visitor?.isSpyingRejected,
    isAccepted: visitor?.isSpyingAccepted,
    isTurnedOn: org?.widgetSettings.viewing === 'ask',
    visitor,
  };
}
